import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { User, MapPin, Bell, LogOut, Trash2, Shield, FileText, Check, Megaphone, Pencil, X, Home, Briefcase, Tag } from 'lucide-react';
import api from '@/utils/api';
import { useAuth } from '@/context/AuthContext';
import { useCart } from '@/context/CartContext';
import { toast } from 'sonner';

const LABEL_PRESETS = [
  { icon: Home, label: 'Casa' },
  { icon: Briefcase, label: 'Trabajo' },
  { icon: Tag, label: 'Otro' },
];

function AddressEditModal({ address, onSave, onClose }) {
  const [label, setLabel] = useState(address?.label || '');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!label.trim()) { toast.error('El nombre es obligatorio.'); return; }
    setSaving(true);
    try {
      await api.put(`/direcciones/${address.id}`, { label: label.trim() });
      toast.success('Dirección actualizada.');
      onSave();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al guardar.');
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 px-4" onClick={onClose}>
      <div data-testid="address-edit-modal" className="w-full max-w-sm bg-zinc-950 border border-zinc-800 rounded-2xl p-5 space-y-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="text-base font-bold">Editar dirección</h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-300"><X size={18} /></button>
        </div>
        <div>
          <label className="text-xs text-zinc-400 mb-1.5 block">Nombre / Etiqueta</label>
          <input
            data-testid="address-label-input"
            value={label}
            onChange={e => setLabel(e.target.value)}
            placeholder="Ej: Casa, Trabajo, Oficina"
            className="w-full bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2.5 text-sm focus:border-[#E84C1C]/50 focus:outline-none"
          />
          <div className="flex gap-2 mt-2">
            {LABEL_PRESETS.map(({ icon: Icon, label: preset }) => (
              <button
                key={preset}
                onClick={() => setLabel(preset)}
                className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border transition-colors ${label === preset ? 'bg-[#E84C1C]/20 border-[#E84C1C]/40 text-[#E84C1C]' : 'bg-zinc-900 border-zinc-800 text-zinc-400 hover:border-zinc-600'}`}
              >
                <Icon size={12} /> {preset}
              </button>
            ))}
          </div>
        </div>
        <p className="text-xs text-zinc-500 truncate">{address?.address_line}</p>
        <button
          data-testid="address-edit-save"
          onClick={submit}
          disabled={saving}
          className="w-full py-2.5 rounded-xl bg-[#E84C1C] text-white font-medium text-sm hover:bg-[#d43f12] disabled:opacity-50 transition-all"
        >
          {saving ? '...' : 'Guardar'}
        </button>
      </div>
    </div>
  );
}

export default function ProfilePage({ onAuthRequired }) {
  const { user, isAuthenticated, logout } = useAuth();
  const { clearCart, setZone } = useCart();
  const navigate = useNavigate();
  const [addresses, setAddresses] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [billing, setBilling] = useState({ cliente_denominacion_ruc: '', cliente_ruc: '', cliente_mail_factura: '' });
  const [billingSaving, setBillingSaving] = useState(false);
  const [marketingPush, setMarketingPush] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);

  const loadAddresses = () => {
    api.get('/direcciones').then(r => setAddresses(r.data.direcciones)).catch(() => {});
  };

  useEffect(() => {
    if (!isAuthenticated) return;
    loadAddresses();
    api.get('/notificaciones/mis-notificaciones?limit=10').then(r => setNotifications(r.data.notificaciones)).catch(() => {});
    api.get('/auth/perfil').then(r => {
      const u = r.data.usuario;
      setBilling({
        cliente_denominacion_ruc: u.cliente_denominacion_ruc || '',
        cliente_ruc: u.cliente_ruc || '',
        cliente_mail_factura: u.cliente_mail_factura || '',
      });
      setMarketingPush(!!u.marketing_push_enabled);
    }).catch(() => {});
  }, [isAuthenticated]);

  const handleLogout = () => {
    logout();
    clearCart();
    setZone(null);
    navigate('/');
  };

  const deleteAddress = async (id) => {
    if (!window.confirm('¿Eliminar esta dirección?')) return;
    try {
      await api.delete(`/direcciones/${id}`);
      setAddresses(prev => prev.filter(a => a.id !== id));
      toast.success('Dirección eliminada.');
    } catch { toast.error('Error al eliminar.'); }
  };

  const markAllRead = async () => {
    try {
      await api.post('/notificaciones/marcar-todas-leidas');
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch { /* ignore */ }
  };

  const saveBilling = async () => {
    setBillingSaving(true);
    try {
      await api.put('/auth/perfil', billing);
      toast.success('Datos de facturación guardados.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al guardar.');
    }
    setBillingSaving(false);
  };

  if (!isAuthenticated) {
    return (
      <div data-testid="profile-page" className="pb-nav flex flex-col items-center justify-center min-h-[60vh] px-4 text-center">
        <div className="w-20 h-20 rounded-full bg-zinc-900 flex items-center justify-center mb-4">
          <User size={32} className="text-zinc-600" />
        </div>
        <h2 className="text-xl font-semibold">Mi cuenta</h2>
        <p className="text-zinc-500 text-sm mt-2">Ingresá para ver tu perfil</p>
        <button data-testid="profile-login-btn" onClick={onAuthRequired} className="mt-6 bg-[#E84C1C] text-white font-medium px-8 py-3 rounded-full hover:bg-[#d43f12] transition-all">
          Iniciar sesión
        </button>
      </div>
    );
  }

  const getLabelIcon = (label) => {
    const lower = (label || '').toLowerCase();
    if (lower.includes('casa') || lower.includes('hogar')) return Home;
    if (lower.includes('trabajo') || lower.includes('oficina')) return Briefcase;
    return MapPin;
  };

  return (
    <div data-testid="profile-page" className="pb-nav">
      {/* User info */}
      <div className="px-4 pt-6 pb-4">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-[#E84C1C]/20 flex items-center justify-center">
            <span className="text-[#E84C1C] text-xl font-bold">{user.nombre?.[0]?.toUpperCase()}</span>
          </div>
          <div>
            <h2 className="text-lg font-bold">{user.nombre}</h2>
            <p className="text-sm text-zinc-400">{user.email}</p>
            {user.telefono && <p className="text-xs text-zinc-500">{user.telefono}</p>}
          </div>
        </div>
      </div>

      {/* Addresses */}
      <section className="mt-4 px-4">
        <h3 className="text-sm font-semibold text-zinc-300 mb-3 flex items-center gap-2">
          <MapPin size={16} className="text-[#E84C1C]" /> Mis direcciones
        </h3>
        <div className="space-y-2">
          {addresses.map(a => {
            const Icon = getLabelIcon(a.label);
            return (
              <div key={a.id} data-testid={`address-row-${a.id}`} className="flex items-center gap-3 p-3 bg-zinc-900/50 rounded-xl border border-zinc-800/30">
                <div className="w-9 h-9 rounded-full bg-[#E84C1C]/10 flex items-center justify-center shrink-0">
                  <Icon size={16} className="text-[#E84C1C]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">{a.label || 'Sin nombre'}</p>
                  <p className="text-xs text-zinc-400 truncate">{a.address_line}</p>
                </div>
                <button
                  data-testid={`address-edit-${a.id}`}
                  onClick={() => setEditingAddress(a)}
                  className="w-8 h-8 rounded-full bg-zinc-800/80 flex items-center justify-center hover:bg-zinc-700 transition-colors"
                >
                  <Pencil size={12} className="text-zinc-400" />
                </button>
                <button
                  data-testid={`address-delete-${a.id}`}
                  onClick={() => deleteAddress(a.id)}
                  className="w-8 h-8 rounded-full bg-red-500/10 flex items-center justify-center hover:bg-red-500/20 transition-colors"
                >
                  <Trash2 size={14} className="text-red-400" />
                </button>
              </div>
            );
          })}
          {addresses.length === 0 && <p className="text-xs text-zinc-500">No tenés direcciones guardadas. Seleccioná una ubicación desde el mapa para guardarla.</p>}
        </div>
      </section>

      {/* Billing */}
      <section className="mt-6 px-4">
        <h3 className="text-sm font-semibold text-zinc-300 mb-1 flex items-center gap-2">
          <FileText size={16} className="text-[#E84C1C]" /> Datos de facturación
        </h3>
        <p className="text-xs text-zinc-500 mb-3">Opcional. Solo completar si necesitás factura.</p>
        <div className="space-y-2">
          <input data-testid="billing-razon-social" value={billing.cliente_denominacion_ruc} onChange={e => setBilling(p => ({ ...p, cliente_denominacion_ruc: e.target.value }))} placeholder="Razón social" className="w-full bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2.5 text-sm placeholder:text-zinc-600 focus:border-[#E84C1C]/50 focus:outline-none" />
          <input data-testid="billing-ruc" value={billing.cliente_ruc} onChange={e => setBilling(p => ({ ...p, cliente_ruc: e.target.value }))} placeholder="RUC" className="w-full bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2.5 text-sm placeholder:text-zinc-600 focus:border-[#E84C1C]/50 focus:outline-none" />
          <input data-testid="billing-email" value={billing.cliente_mail_factura} onChange={e => setBilling(p => ({ ...p, cliente_mail_factura: e.target.value }))} placeholder="Email para factura" type="email" className="w-full bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2.5 text-sm placeholder:text-zinc-600 focus:border-[#E84C1C]/50 focus:outline-none" />
          <button data-testid="billing-save-btn" onClick={saveBilling} disabled={billingSaving} className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-[#E84C1C] text-white text-sm font-medium hover:bg-[#d43f12] transition-colors disabled:opacity-50">
            {billingSaving ? '...' : <><Check size={16} /> Guardar</>}
          </button>
        </div>
      </section>

      {/* Marketing Push Preference */}
      <section className="mt-6 px-4">
        <h3 className="text-sm font-semibold text-zinc-300 mb-1 flex items-center gap-2">
          <Megaphone size={16} className="text-[#E84C1C]" /> Promociones
        </h3>
        <p className="text-xs text-zinc-500 mb-3">Te avisaremos ofertas y promos del día.</p>
        <button
          data-testid="marketing-push-toggle"
          onClick={async () => {
            const next = !marketingPush;
            setMarketingPush(next);
            try {
              await api.put('/auth/perfil', { marketing_push_enabled: next });
              toast.success(next ? 'Notificaciones de promos activadas.' : 'Notificaciones de promos desactivadas.');
            } catch {
              setMarketingPush(!next);
              toast.error('Error al guardar preferencia.');
            }
          }}
          className={`w-full flex items-center justify-between p-3.5 rounded-xl border transition-colors ${marketingPush ? 'bg-[#E84C1C]/10 border-[#E84C1C]/30' : 'bg-zinc-900/50 border-zinc-800/50'}`}
        >
          <span className="text-sm font-medium">Recibir promociones</span>
          <div className={`w-10 h-6 rounded-full relative transition-colors ${marketingPush ? 'bg-[#E84C1C]' : 'bg-zinc-700'}`}>
            <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${marketingPush ? 'left-5' : 'left-1'}`} />
          </div>
        </button>
      </section>

      {/* Notifications */}
      <section className="mt-6 px-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-zinc-300 flex items-center gap-2">
            <Bell size={16} className="text-[#E84C1C]" /> Notificaciones
          </h3>
          {notifications.some(n => !n.read) && (
            <button onClick={markAllRead} className="text-xs text-[#E84C1C]">Marcar leídas</button>
          )}
        </div>
        <div className="space-y-2">
          {notifications.map(n => (
            <div key={n.id} className={`p-3 rounded-xl border ${n.read ? 'bg-zinc-900/30 border-zinc-800/20' : 'bg-zinc-900/50 border-zinc-800/50'}`}>
              <p className="text-sm font-medium">{n.title}</p>
              <p className="text-xs text-zinc-400 mt-0.5">{n.message}</p>
              <p className="text-[10px] text-zinc-600 mt-1">{new Date(n.created_at).toLocaleString('es-PY')}</p>
            </div>
          ))}
          {notifications.length === 0 && <p className="text-xs text-zinc-500">Sin notificaciones.</p>}
        </div>
      </section>

      {/* Admin link */}
      {(user.roles?.includes('admin_global') || user.roles?.includes('recepcion_sucursal') || user.roles?.includes('manager_sucursal') || user.roles?.includes('operador_pedidos')) && (
        <div className="mt-6 px-4">
          <Link to="/admin" data-testid="profile-admin-link" className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-[#E84C1C]/10 border border-[#E84C1C]/20 text-[#E84C1C] font-medium hover:bg-[#E84C1C]/20 transition-colors">
            <Shield size={18} /> Panel de Administración
          </Link>
        </div>
      )}

      {/* Logout */}
      <div className="mt-8 px-4 mb-6">
        <button data-testid="profile-logout-btn" onClick={handleLogout} className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border border-zinc-800 text-zinc-400 hover:text-red-400 hover:border-red-500/30 transition-colors">
          <LogOut size={18} /> Cerrar sesión
        </button>
      </div>

      {/* Address edit modal */}
      {editingAddress && (
        <AddressEditModal
          address={editingAddress}
          onSave={() => { setEditingAddress(null); loadAddresses(); }}
          onClose={() => setEditingAddress(null)}
        />
      )}
    </div>
  );
}
